<template>
  <a-form-item
    :name="startName"
    :required="required"
  >
    <template #label>
      <slot name="start-label">
        {{ startLabel }}
      </slot>
    </template>
    <a-tooltip
      :title="tooltipText"
      placement="top"
    >
      <a-date-picker
        ref="startPickerRef"
        v-model:value="startModel"
        :disabled-date="disableStartDate"
        :format="format"
        :valueFormat="valueFormat"
        :class="startClass"
        :placeholder="startPlaceholder"
        :disabled="disabled"
        :show-time="showTime"
        @openChange="handleStartOpenChange"
      />
    </a-tooltip>
  </a-form-item>

  <a-form-item
    :label="endLabel"
    :name="endName"
    :required="required"
  >
    <template #label>
      <slot name="end-label">
        {{ endLabel }}
      </slot>
    </template>
    <a-tooltip
      :title="tooltipText"
      placement="top"
    >
      <a-date-picker
        ref="endPickerRef"
        v-model:value="endModel"
        :disabled-date="disableEndDate"
        :format="format"
        :valueFormat="valueFormat"
        :class="endClass"
        :placeholder="endPlaceholder"
        :disabled="disabled"
        :open="endOpen"
        :show-time="showTime"
        @openChange="handleEndOpenChange"
      />
    </a-tooltip>
  </a-form-item>
</template>

<script setup lang="ts">
import { computed, nextTick, ref } from "vue";
import dayjs from "dayjs";
import { useStore } from "vuex";

import { PlanEspecial } from "@/app/common/types";

import { usePlanSearchRange } from "@/app/shared/composables/usePlanDateRange";
import { usePlanDateRangeValidation } from "@/app/shared/composables/usePlanDateRangeValidation";

import { RangeConfigKey } from "@/app/shared/utils/planDatePicker";

interface Props {
  startDate: string | null | undefined;
  endDate: string | null | undefined;

  configKey?: RangeConfigKey;

  defaultLimit?: number;

  allowSameDate?: boolean;

  required?: boolean;
  disabled?: boolean;

  format?: string;
  valueFormat?: string;

  startLabel?: string;
  endLabel?: string;

  startName?: string;
  endName?: string;

  startPlaceholder?: string;
  endPlaceholder?: string;

  startClass?: string;
  endClass?: string;

  showTime?: boolean;
}

const props = withDefaults(
  defineProps<Props>(),
  {
    defaultLimit: 31,

    allowSameDate: true,

    required: true,
    disabled: false,

    configKey: RangeConfigKey.MessagingSearchRange,
    
    format: "DD/MM/YYYY",
    valueFormat: "YYYY-MM-DD",

    startLabel: "Fecha de inicio",
    endLabel: "Fecha de fin",

    startName: "startDate",
    endName: "endDate",

    startPlaceholder: "Seleccionar fecha",
    endPlaceholder: "Seleccionar fecha",

    startClass: "",
    endClass: "",
    showTime: false,
  }
);

const emit = defineEmits<{
  (
    e: "update:startDate",
    value: string | null
  ): void;

  (
    e: "update:endDate",
    value: string | null
  ): void;
}>();

const startPickerRef = ref();
const endPickerRef = ref();

const endOpen = ref(false);

const startModel = computed({
  get: () => props.startDate,
  set: (value) =>
    emit("update:startDate", value),
});

const endModel = computed({
  get: () => props.endDate,
  set: (value) =>
    emit("update:endDate", value),
});

const {
  rangeLimit,
  typeRange,
  rangeLimitText,
} = usePlanSearchRange(
  props.defaultLimit,
  props.configKey,
);

const store = useStore();

const userData = computed(
  () => store.getters["getDataUser"]
);

const plan = computed(
  () => userData.value?.change_custom_plan?.plan
);

const isUnlimitedPlan = computed(
  () => plan.value === PlanEspecial
);

const { isInvalidRange  } =
  usePlanDateRangeValidation(
    rangeLimit,
    typeRange
  );

const disableStartDate = (startDate: any) => {
  return isInvalidRange(
    startDate,
    props.endDate
  );
};

const disableEndDate = (endDate: any) => {
  return isInvalidRange(
    props.startDate,
    endDate,
    props.allowSameDate
  );
};

const tooltipText = computed(() => {
  if (isUnlimitedPlan.value) {
    return "";
  }

  return `Puedes seleccionar un rango máximo de ${rangeLimitText.value}`;
});

const handleStartOpenChange = (
  open: boolean
) => {
  if (!open) {
    endOpen.value = true;
  }
};

const handleEndOpenChange = (
  open: boolean
) => {
  endOpen.value = open;
};
</script>